import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { useAuth } from '../context/AuthContext';
import { usePlayer } from '../context/PlayerContext';
import { toast } from 'sonner';
import { API, API_BASE_URL } from '../config/api';
import { SubscribeButton } from './SubscribeButton';
import { RequestButton } from './RequestButton';
import { MessageButton } from './MessageButton';
import { ShareButton, ShareIconButton } from './ShareSheet';
import { TopTippersLeaderboard, TopFansLeaderboard } from './LeaderboardWidgets';
import {
  Play, Pause, Heart, Share2, Users, UserPlus, UserCheck,
  Verified, MapPin, Music, Video, FileText, ExternalLink,
  MoreHorizontal, Clock, TrendingUp, Award, Star,
  Instagram, Youtube, Twitter, Globe, ChevronRight,
  Volume2, SkipForward, MessageCircle, Radio,
  Copy, Flag, Ban, BellOff, Link2, Image, ZoomIn, Trash2, Plus,
  Bot
} from 'lucide-react';
import PhotoLightboxComponent from './PhotoLightbox';
import { GamificationCard, GamificationMini } from './GamificationProfile';
import { WeeklyGrowthBadge } from './LiveActivityStrip';
import { ListenerPhotoLibrary } from './ListenerPhotoLibrary';
import { InfluenceProfileCard, TopSupportersCard } from './InfluenceSystem';
import { ProfileBackground } from './ProfileBackground';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

// Helper function to format duration from seconds to MM:SS
const formatDuration = (seconds) => {
  if (!seconds || seconds === 0) return '0:00';
  if (typeof seconds === 'string' && seconds.includes(':')) return seconds; // Already formatted
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const TIER_CONFIG = {
  starter: { label: 'ARTIST', color: 'bg-gradient-to-r from-gray-600 to-gray-700', textColor: 'text-white' },
  pro: { label: 'PRO', color: 'bg-gradient-to-r from-orange-500 to-amber-500', textColor: 'text-white' },
  label: { label: 'ENTERPRISE', color: 'bg-gradient-to-r from-purple-500 to-violet-600', textColor: 'text-white' },
  enterprise: { label: 'ENTERPRISE', color: 'bg-gradient-to-r from-purple-500 to-violet-600', textColor: 'text-white' }
};

const SOCIAL_ICONS = {
  instagram: Instagram,
  youtube: Youtube,
  twitter: Twitter,
  website: Globe,
  tiktok: Music,
  soundcloud: Music,
  spotify: Music
};

// Featured Content Card Component
const FeaturedCard = ({ title, subtitle, image, onClick, type }) => (
  <div 
    className="flex-shrink-0 w-36 cursor-pointer group"
    onClick={onClick}
  >
    <div className="aspect-square rounded-xl overflow-hidden mb-2 relative bg-muted/30">
      {image ? (
        <img src={image} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          {type === 'video' ? <Video className="w-8 h-8 text-muted-foreground" /> : <Music className="w-8 h-8 text-muted-foreground" />}
        </div>
      )}
      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
        <Play className="w-10 h-10 text-white" fill="white" />
      </div>
    </div>
    <p className="text-xs font-medium text-foreground truncate">{title}</p>
    <p className="text-xs text-muted-foreground truncate">{subtitle}</p>
  </div>
);

// Track Row Component
const TrackRow = ({ track, index, isPlaying, onPlay, onLike, isLiked }) => (
  <div 
    className={`flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl transition-all group cursor-pointer active:scale-[0.98] ${
      isPlaying 
        ? 'bg-secondary/10 ring-1 ring-secondary/20 shadow-md shadow-secondary/5' 
        : 'hover:bg-muted/40'
    }`}
    onClick={() => onPlay(track)}
    data-testid={`track-row-${track.track_id}`}
  >
    {/* Track Number / Play Indicator */}
    <div className="w-7 flex-shrink-0 flex items-center justify-center">
      {isPlaying ? (
        <div className="flex items-end gap-[2px] h-4" data-testid="track-playing-indicator">
          <span className="w-[3px] bg-secondary rounded-full animate-pulse" style={{ height: '60%', animationDelay: '0ms' }} />
          <span className="w-[3px] bg-secondary rounded-full animate-pulse" style={{ height: '100%', animationDelay: '150ms' }} />
          <span className="w-[3px] bg-secondary rounded-full animate-pulse" style={{ height: '40%', animationDelay: '300ms' }} />
          <span className="w-[3px] bg-secondary rounded-full animate-pulse" style={{ height: '80%', animationDelay: '100ms' }} />
        </div>
      ) : (
        <>
          <span className="text-muted-foreground text-sm tabular-nums group-hover:hidden">{index + 1}</span>
          <Play className="w-4 h-4 text-foreground hidden group-hover:block" />
        </>
      )}
    </div>
    
    {/* Cover Art — 64px on mobile, 72px on desktop */}
    <div className="w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-lg bg-muted/50 overflow-hidden flex-shrink-0 shadow-sm">
      {track.cover_art_url ? (
        <img src={track.cover_art_url} alt="" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted/80 to-muted/40">
          <Music className="w-6 h-6 text-muted-foreground/60" />
        </div>
      )}
    </div>
    
    {/* Track Info — 2-line title, artist, metadata */}
    <div className="flex-1 min-w-0 py-0.5">
      <p className={`text-sm sm:text-base font-semibold leading-snug line-clamp-2 ${
        isPlaying ? 'text-secondary' : 'text-foreground'
      }`}>
        {track.title}
      </p>
      <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 truncate">
        {track.artist_name}
      </p>
      {/* Metadata row — plays + duration on mobile */}
      <div className="flex items-center gap-2 mt-1 text-[11px] text-muted-foreground/70">
        {track.plays > 0 && (
          <span>{track.plays?.toLocaleString()} plays</span>
        )}
        <span className="sm:hidden">{formatDuration(track.duration)}</span>
      </div>
    </div>
    
    {/* Actions — clean, minimal */}
    <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
      <button 
        onClick={(e) => { e.stopPropagation(); onLike(track); }}
        className={`p-2 rounded-full transition-colors ${isLiked ? 'text-red-500' : 'text-muted-foreground hover:text-foreground'}`}
        data-testid={`track-like-${track.track_id}`}
      >
        <Heart className="w-4 h-4 sm:w-[18px] sm:h-[18px]" fill={isLiked ? 'currentColor' : 'none'} />
      </button>
      <span className="text-xs text-muted-foreground tabular-nums hidden sm:inline min-w-[36px] text-right">
        {formatDuration(track.duration)}
      </span>
      {/* More menu — holds Share + Request */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button 
            onClick={(e) => e.stopPropagation()}
            className="p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
            data-testid={`track-more-${track.track_id}`}
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); }} className="gap-2">
            <ShareIconButton
              url={`/track/${track.track_id}`}
              title={`${track.title} by ${track.artist_name}`}
              className="text-muted-foreground p-0"
            />
            <span>Share</span>
          </DropdownMenuItem>
          <DropdownMenuItem className="gap-2">
            <RequestButton
              targetType="song"
              targetId={track.track_id}
              targetName={track.title}
              creatorName={track.artist_name}
              variant="ghost"
              size="sm"
              showLabel={true}
              className="text-orange-500 hover:text-orange-400 p-0 h-auto"
            />
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  </div>
);

export default function PremiumArtistProfile({ 
  profile, 
  isOwnProfile, 
  isFollowing, 
  onFollow, 
  followerCount, 
  followingCount,
  onShowFollowers,
  onShowFollowing 
}) {
  const navigate = useNavigate();
  const { user: currentUser, isAuthenticated, getAuthHeaders } = useAuth();
  const { playTrack: playerPlayTrack, currentTrack, isPlaying: playerIsPlaying } = usePlayer();
  
  const [tracks, setTracks] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [videos, setVideos] = useState([]);
  const [posts, setPosts] = useState([]);
  const [replays, setReplays] = useState([]);
  const [playingReplay, setPlayingReplay] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [photoLightbox, setPhotoLightbox] = useState({ open: false });
  const [gamificationProfile, setGamificationProfile] = useState(null);

  const _buildGalleryImages = () => {
    const images = [];
    if (profile.avatar_url) {
      images.push(profile.avatar_url.startsWith('http') ? profile.avatar_url : `${API.replace('/api', '')}${profile.avatar_url}`);
    }
    if (profile.banner_url) {
      images.push(profile.banner_url.startsWith('http') ? profile.banner_url : `${API.replace('/api', '')}${profile.banner_url}`);
    }
    photos.forEach(p => {
      images.push(p.photo_url.startsWith('http') ? p.photo_url : `${API.replace('/api', '')}${p.photo_url}`);
    });
    return images;
  };
  const [featuredTrack, setFeaturedTrack] = useState(null);
  const [latestRelease, setLatestRelease] = useState(null);
  const [topSong, setTopSong] = useState(null);
  const [chartPosition, setChartPosition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [likedTracks, setLikedTracks] = useState(new Set());
  const [activeSection, setActiveSection] = useState('music');
  const [sortBy, setSortBy] = useState('newest');
  
  const tierConfig = TIER_CONFIG[profile.artist_tier] || TIER_CONFIG.starter;
  const artistId = profile.artist_id || profile.user_id;

  // Handle post-subscription redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('subscribed') === 'true') {
      toast.success('Subscription activated! You now have access to exclusive content.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);
  
  useEffect(() => {
    fetchAllContent();
  }, [profile.user_id]);
  
  const fetchAllContent = async () => {
    try {
      // Fetch tracks - use user_id since tracks are stored with user_id
      const userId = profile.user_id;
      const tracksRes = await fetch(`${API}/tracks?user_id=${userId}&limit=50`);
      if (tracksRes.ok) {
        const data = await tracksRes.json();
        // Backend returns array directly
        const trackList = Array.isArray(data) ? data : (data.tracks || []);
        console.log('[PremiumProfile] Fetched tracks:', trackList.length);
        setTracks(trackList);
        
        // Set featured content
        if (trackList.length > 0) {
          // Featured track - could be pinned or first track
          const featured = trackList.find(t => t.is_featured) || trackList[0];
          setFeaturedTrack(featured);
          
          // Latest release - most recent by date
          const sorted = [...trackList].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
          setLatestRelease(sorted[0]);
          
          // Top song - most plays
          const byPlays = [...trackList].sort((a, b) => (b.plays || 0) - (a.plays || 0));
          setTopSong(byPlays[0]);
        }
      }
      
      // Albums endpoint doesn't exist - skip for now
      // Albums would need a separate feature implementation
      
      // Fetch videos using correct endpoint
      const videosRes = await fetch(`${API}/videos?user_id=${userId}&limit=10`);
      if (videosRes.ok) {
        const data = await videosRes.json();
        const videoList = Array.isArray(data) ? data : (data.videos || []);
        console.log('[PremiumProfile] Fetched videos:', videoList.length);
        setVideos(videoList);
      }
      
      // Fetch posts using correct endpoint
      const postsRes = await fetch(`${API}/posts/user/${userId}?visibility=public&limit=10`);
      if (postsRes.ok) {
        const data = await postsRes.json();
        const postList = data.posts || [];
        setPosts(postList);
      }
      
      // Fetch live replays
      try {
        const replaysRes = await fetch(`${API}/live/replays/user/${userId}`);
        if (replaysRes.ok) {
          const data = await replaysRes.json();
          setReplays(data.replays || []);
        }
      } catch {}
      
      // Fetch photo gallery
      try {
        const photosRes = await fetch(`${API}/users/${userId}/photos`);
        if (photosRes.ok) {
          const data = await photosRes.json();
          setPhotos(data.photos || []);
        }
      } catch {}
      
      // Chart position - skip if endpoint not available
      // This is optional feature

      // Fetch gamification profile for this user
      try {
        const gamRes = await fetch(`${API}/gamification/profile/${userId}`);
        if (gamRes.ok) {
          setGamificationProfile(await gamRes.json());
        }
      } catch {}
      
    } catch (err) {
      console.error('Error fetching content:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Use the global player context for track playback
  const handlePlayTrack = (track) => {
    playerPlayTrack(track, tracks);
  };
  
  const playLatestRelease = () => {
    if (latestRelease) {
      handlePlayTrack(latestRelease);
      toast.success(`Now playing: ${latestRelease.title}`);
    } else if (tracks.length > 0) {
      handlePlayTrack(tracks[0]);
      toast.success(`Now playing: ${tracks[0].title}`);
    } else {
      toast.error('No tracks available to play');
    }
  };
  
  // Check if a track is currently playing
  const isTrackPlaying = (track) => {
    return currentTrack?.track_id === track.track_id && playerIsPlaying;
  };
  
  const toggleLike = async (track) => {
    if (!isAuthenticated) {
      toast.error('Please log in to like tracks');
      return;
    }
    
    try {
      const res = await fetch(`${API}/tracks/${track.track_id}/like`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        setLikedTracks(prev => {
          const newSet = new Set(prev);
          if (newSet.has(track.track_id)) {
            newSet.delete(track.track_id);
          } else {
            newSet.add(track.track_id);
          }
          return newSet;
        });
      }
    } catch (err) {
      console.error('Like error:', err);
    }
  };
  
  const handleShare = async () => {
    const url = `${window.location.origin}/u/${profile.handle}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Profile link copied!');
    } catch {
      toast.error('Failed to copy link');
    }
  };
  
  const getSortedTracks = () => {
    let sorted = [...tracks];
    switch (sortBy) {
      case 'newest':
        return sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      case 'popular':
        return sorted.sort((a, b) => (b.plays || 0) - (a.plays || 0));
      case 'liked':
        return sorted.sort((a, b) => (b.likes || 0) - (a.likes || 0));
      default:
        return sorted;
    }
  };

  return (
    <ProfileBackground userId={profile.user_id}>
    <div className="min-h-screen bg-background" data-testid="premium-artist-profile">
      {/* ─── Cover Image + Giant Profile Photo ─── */}
      <div className="relative" data-testid="profile-header">
        {/* Cover Image Area */}
        <div className="relative w-full h-48 sm:h-56 md:h-72 lg:h-80 overflow-hidden bg-muted/30">
          {profile.banner_url ? (
            <img 
              src={profile.banner_url.startsWith('http') ? profile.banner_url : `${API.replace('/api', '')}${profile.banner_url}`}
              alt="Cover"
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-purple-900/50 via-background/80 to-orange-900/30">
              {/* Subtle name watermark */}
              <div className="absolute inset-0 overflow-hidden opacity-[0.04] select-none pointer-events-none" aria-hidden="true">
                <div className="absolute inset-0 flex flex-wrap items-center justify-center gap-x-12 gap-y-6 -rotate-12 scale-125">
                  {Array.from({ length: 12 }).map((_, i) => (
                    <span key={i} className="text-foreground text-5xl md:text-7xl font-black uppercase whitespace-nowrap tracking-widest">
                      {profile.name || profile.stage_name}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
          {/* Bottom gradient fade into background */}
          <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background to-transparent" />
          
          {/* Edit Cover button — own profile only */}
          {isOwnProfile && (
            <button
              onClick={() => navigate('/settings')}
              className="absolute top-3 right-3 z-10 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-black/50 hover:bg-black/70 text-white text-xs font-medium backdrop-blur-sm transition-all"
              data-testid="edit-cover-btn"
            >
              <Image className="w-3.5 h-3.5" />
              {profile.banner_url ? 'Edit Cover' : 'Add Cover'}
            </button>
          )}
        </div>

        {/* Profile Photo + Info — overlaps cover image */}
        <div className="relative -mt-28 sm:-mt-32 md:-mt-40 px-4 sm:px-6 md:px-10 pb-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-center sm:items-end gap-4 sm:gap-6">
              
              {/* Giant Profile Photo */}
              <div className="relative flex-shrink-0 z-10">
                <div
                  className="w-48 h-48 sm:w-56 sm:h-56 md:w-64 md:h-64 lg:w-72 lg:h-72 rounded-2xl border-4 border-background shadow-2xl shadow-black/40 overflow-hidden bg-muted/50 cursor-pointer hover:ring-2 hover:ring-secondary/50 transition-all duration-300 ring-1 ring-white/10"
                  onClick={() => setPhotoLightbox({ open: true })}
                  role="button"
                  tabIndex={0}
                  data-testid="profile-avatar-clickable"
                >
                  {profile.avatar_url ? (
                    <img 
                      src={profile.avatar_url.startsWith('http') ? profile.avatar_url : `${API.replace('/api', '')}${profile.avatar_url}`}
                      alt={profile.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-500 to-orange-500">
                      <Music className="w-16 h-16 sm:w-20 sm:h-20 text-white" />
                    </div>
                  )}
                </div>
                {/* Verified Badge */}
                {profile.is_verified && (
                  <div className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-secondary flex items-center justify-center border-3 border-background shadow-lg z-20">
                    <Verified className="w-5 h-5 text-white" />
                  </div>
                )}
              </div>
              
              {/* Artist Info */}
              <div className="flex-1 min-w-0 text-center sm:text-left space-y-2 pb-1">
                {/* Name */}
                <div className="flex items-center gap-3 flex-wrap justify-center sm:justify-start">
                  <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-foreground tracking-tight drop-shadow-sm">
                    {profile.name || profile.stage_name}
                  </h1>
                  {profile.is_verified && (
                    <Verified className="w-6 h-6 md:w-7 md:h-7 text-secondary flex-shrink-0 hidden sm:block" />
                  )}
                </div>
                
                {/* Badges */}
                <div className="flex items-center gap-2 flex-wrap justify-center sm:justify-start">
                  {gamificationProfile && <GamificationMini profile={gamificationProfile} />}
                  <span className={`px-3 py-1 rounded-full text-[11px] font-bold ${tierConfig.color} ${tierConfig.textColor} shadow-sm`}>
                    {tierConfig.label}
                  </span>
                  {profile.neighborhood && (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground bg-muted/30 px-2.5 py-1 rounded-full">
                      <MapPin className="w-3 h-3" />
                      {profile.neighborhood}
                    </span>
                  )}
                  {chartPosition && (
                    <span className="flex items-center gap-1 text-xs font-semibold text-secondary bg-secondary/10 px-2.5 py-1 rounded-full">
                      <TrendingUp className="w-3 h-3" />
                      #{chartPosition} on Charts
                    </span>
                  )}
                </div>
                
                {/* Stats */}
                <div className="flex items-center gap-5 text-sm pt-1 justify-center sm:justify-start">
                  <button onClick={onShowFollowers} className="hover:text-secondary transition-colors group">
                    <span className="font-bold text-foreground group-hover:text-secondary">{followerCount?.toLocaleString() || 0}</span>
                    <span className="text-muted-foreground ml-1">Followers</span>
                  </button>
                  <button onClick={onShowFollowing} className="hover:text-secondary transition-colors group">
                    <span className="font-bold text-foreground group-hover:text-secondary">{followingCount?.toLocaleString() || 0}</span>
                    <span className="text-muted-foreground ml-1">Following</span>
                  </button>
                  {tracks.length > 0 && (
                    <span>
                      <span className="font-bold text-foreground">{tracks.length}</span>
                      <span className="text-muted-foreground ml-1">Tracks</span>
                    </span>
                  )}
                  <WeeklyGrowthBadge userId={profile.user_id} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Action Buttons Bar */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-3 flex-wrap">
            {/* Follow Button */}
            {!isOwnProfile && (
              <Button
                onClick={onFollow}
                className={isFollowing 
                  ? "bg-muted/50 hover:bg-muted/60 text-foreground" 
                  : "bg-secondary hover:bg-secondary/90 text-white"
                }
                data-testid="follow-btn"
              >
                {isFollowing ? (
                  <>
                    <UserCheck className="w-4 h-4 mr-2" />
                    Following
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Follow
                  </>
                )}
              </Button>
            )}
            
            {/* Request Button - Primary CTA */}
            {!isOwnProfile && (
              <RequestButton
                targetType="creator"
                targetId={profile.artist_id || profile.user_id}
                targetName={profile.name || profile.stage_name || profile.handle}
                creatorName={profile.name || profile.stage_name}
                variant="default"
                className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white"
              />
            )}
            
            {/* Message Button - Direct Messaging */}
            {!isOwnProfile && profile.user_id && (
              <MessageButton
                targetUserId={profile.user_id}
                targetName={profile.name || profile.stage_name || profile.handle}
                variant="outline"
                className="border-border text-foreground hover:bg-muted/50"
              />
            )}
            
            {/* Play Latest Release */}
            <Button
              onClick={playLatestRelease}
              className="bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 text-white"
              data-testid="play-latest-btn"
            >
              <Play className="w-4 h-4 mr-2" fill="white" />
              Play Latest Release
            </Button>
            
            {/* Subscribe (if has subscription) */}
            {profile.artist_id && (
              <SubscribeButton 
                artistId={profile.artist_id} 
                artistName={profile.name || profile.stage_name || profile.handle}
                className="border-border hover:bg-muted/50"
              />
            )}
            
            {/* Share */}
            <ShareButton
              url={`/u/${profile.handle || profile.user_id}`}
              title={`${profile.name || profile.stage_name || profile.handle} on DMV Soundstage`}
              description={profile.bio?.substring(0, 120)}
            />
            
            {/* More Options */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="hover:bg-muted/50" data-testid="profile-more-options">
                  <MoreHorizontal className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem 
                  onClick={() => {
                    try { navigator.clipboard.writeText(window.location.href).catch(() => {}); } catch {}
                    toast.success('Profile link copied!');
                  }}
                  data-testid="copy-profile-link"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Profile Link
                </DropdownMenuItem>
                {currentUser && currentUser.user_id !== profile.user_id && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={async () => {
                        try {
                          const res = await fetch(`${API}/users/${profile.user_id}/report`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                            body: JSON.stringify({ reason: 'inappropriate_content' }),
                          });
                          if (res.ok) toast.success('Report submitted. We\'ll review this profile.');
                          else toast.error('Could not submit report');
                        } catch { toast.error('Failed to submit report'); }
                      }}
                      data-testid="report-user"
                    >
                      <Flag className="w-4 h-4 mr-2" />
                      Report User
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={async () => {
                        try {
                          const res = await fetch(`${API}/users/${profile.user_id}/block`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                          });
                          if (res.ok) toast.success('User blocked');
                          else toast.error('Could not block user');
                        } catch { toast.error('Failed to block user'); }
                      }}
                      className="text-red-400 focus:text-red-400"
                      data-testid="block-user"
                    >
                      <Ban className="w-4 h-4 mr-2" />
                      Block User
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      
      {/* Featured Content Row */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <h2 className="text-lg font-semibold text-foreground mb-4">Featured</h2>
        <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
          {featuredTrack && (
            <FeaturedCard 
              title="Featured Track"
              subtitle={featuredTrack.title}
              image={featuredTrack.cover_art_url}
              onClick={() => handlePlayTrack(featuredTrack)}
              type="track"
            />
          )}
          {latestRelease && latestRelease.track_id !== featuredTrack?.track_id && (
            <FeaturedCard 
              title="Latest Release"
              subtitle={latestRelease.title}
              image={latestRelease.cover_art_url}
              onClick={() => handlePlayTrack(latestRelease)}
              type="track"
            />
          )}
          {topSong && topSong.track_id !== featuredTrack?.track_id && topSong.track_id !== latestRelease?.track_id && (
            <FeaturedCard 
              title="Top Song"
              subtitle={topSong.title}
              image={topSong.cover_art_url}
              onClick={() => handlePlayTrack(topSong)}
              type="track"
            />
          )}
          {videos[0] && (
            <FeaturedCard 
              title="Featured Video"
              subtitle={videos[0].title || 'Watch Now'}
              image={videos[0].thumbnail_url}
              onClick={() => navigate(`/video/${videos[0].video_id}`)}
              type="video"
            />
          )}
          {posts[0] && (
            <FeaturedCard 
              title="Spotlight Reel"
              subtitle="Latest Post"
              image={posts[0].media_urls?.[0] || posts[0].content_image_url}
              onClick={() => navigate(`/post/${posts[0].post_id}`)}
              type="post"
            />
          )}
        </div>
      </div>
      
      {/* Main Content Sections */}
      <div className="max-w-7xl mx-auto px-6 pb-20">
        {/* Section Navigation */}
        <div className="flex items-center gap-4 border-b border-border mb-6 overflow-x-auto scrollbar-hide -mx-6 px-6">
          {['music', 'albums', 'videos', 'replays', 'photos', 'spotlight', 'about'].map((section) => (
            <button
              key={section}
              onClick={() => setActiveSection(section)}
              className={`pb-4 px-1 text-sm font-medium transition-colors relative whitespace-nowrap shrink-0 ${
                activeSection === section 
                  ? 'text-foreground' 
                  : 'text-muted-foreground/70 hover:text-muted-foreground'
              }`}
            >
              {section.charAt(0).toUpperCase() + section.slice(1)}
              {activeSection === section && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-secondary" />
              )}
            </button>
          ))}
        </div>
        
        {/* Music Section */}
        {activeSection === 'music' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-foreground">Music</h2>
              <select 
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="bg-muted/30 border border-border rounded-lg px-3 py-2 text-sm text-foreground"
              >
                <option value="newest">Newest</option>
                <option value="popular">Most Played</option>
                <option value="liked">Most Liked</option>
              </select>
            </div>
            
            <div className="bg-muted/20 rounded-xl p-1.5 sm:p-2 space-y-1">
              {getSortedTracks().map((track, index) => (
                <TrackRow 
                  key={track.track_id}
                  track={track}
                  index={index}
                  isPlaying={isTrackPlaying(track)}
                  onPlay={handlePlayTrack}
                  onLike={toggleLike}
                  isLiked={likedTracks.has(track.track_id)}
                />
              ))}
              {tracks.length === 0 && (
                <div className="text-center py-12 text-muted-foreground/70">
                  <Music className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No tracks yet</p>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Albums Section */}
        {activeSection === 'albums' && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-foreground mb-4">Albums & EPs</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {albums.map((album) => (
                <div 
                  key={album.album_id}
                  className="group cursor-pointer"
                  onClick={() => navigate(`/albums/${album.album_id}`)}
                >
                  <div className="aspect-square rounded-lg overflow-hidden bg-muted/30 mb-2 relative">
                    {album.cover_art_url ? (
                      <img src={album.cover_art_url} alt={album.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Music className="w-12 h-12 text-muted-foreground/50" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-background/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Play className="w-12 h-12 text-white" fill="white" />
                    </div>
                  </div>
                  <p className="font-medium text-foreground truncate">{album.title}</p>
                  <p className="text-xs text-muted-foreground/70">{album.type || 'Album'} • {album.track_count || 0} tracks</p>
                </div>
              ))}
              {albums.length === 0 && (
                <div className="col-span-full text-center py-12 text-muted-foreground/70">
                  <Music className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No albums yet</p>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Videos Section */}
        {activeSection === 'videos' && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-foreground mb-4">Videos</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {videos.map((video) => (
                <div 
                  key={video.video_id}
                  className="group cursor-pointer"
                  onClick={() => navigate(`/video/${video.video_id}`)}
                >
                  <div className="aspect-video rounded-lg overflow-hidden bg-muted/30 mb-2 relative">
                    {video.thumbnail_url ? (
                      <img src={video.thumbnail_url} alt={video.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Video className="w-12 h-12 text-muted-foreground/50" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-background/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Play className="w-12 h-12 text-white" fill="white" />
                    </div>
                    <div className="absolute bottom-2 right-2 bg-background/80 px-2 py-1 rounded text-xs text-white">
                      {formatDuration(video.duration)}
                    </div>
                  </div>
                  <p className="font-medium text-foreground truncate">{video.title}</p>
                  <p className="text-xs text-muted-foreground/70">{video.views?.toLocaleString() || 0} views</p>
                </div>
              ))}
              {videos.length === 0 && (
                <div className="col-span-full text-center py-12 text-muted-foreground/70">
                  <Video className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No videos yet</p>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Replays Section */}
        {activeSection === 'replays' && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-foreground mb-4">Live Replays</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {replays.map((replay) => (
                <Link 
                  key={replay.replay_id || replay.stream_id}
                  to={`/live/replay/${replay.replay_id || replay.stream_id}`}
                  className="block"
                >
                <Card 
                  className="bg-muted/30 border-border overflow-hidden cursor-pointer hover:bg-muted/50 hover:border-border transition-all"
                  data-testid="replay-card"
                >
                  {replay.cover_image ? (
                    <div className="aspect-video relative">
                      <img src={replay.cover_image.startsWith('/') ? `${API_BASE_URL}${replay.cover_image}` : replay.cover_image} alt={replay.title || 'Live Replay'} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors">
                        <div className="w-12 h-12 rounded-full bg-red-500/90 flex items-center justify-center">
                          <Play className="w-6 h-6 text-white ml-0.5" />
                        </div>
                      </div>
                      {replay.duration_seconds && (
                        <span className="absolute bottom-2 right-2 bg-background/70 text-foreground text-xs px-2 py-0.5 rounded">
                          {Math.floor(replay.duration_seconds / 60)}:{String(replay.duration_seconds % 60).padStart(2, '0')}
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="aspect-video relative bg-gradient-to-br from-purple-900/50 to-indigo-600/30 flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full bg-red-500/90 flex items-center justify-center">
                        <Play className="w-6 h-6 text-white ml-0.5" />
                      </div>
                    </div>
                  )}
                  <div className="p-4">
                    <h3 className="font-semibold text-foreground">{replay.title || 'Live Stream'}</h3>
                    <p className="text-sm text-muted-foreground/80 mt-1">
                      {replay.created_at ? new Date(replay.created_at).toLocaleDateString() : ''}
                      {replay.viewer_count ? ` \u2022 ${replay.viewer_count} viewers` : ''}
                      {replay.views ? ` \u2022 ${replay.views} views` : ''}
                    </p>
                  </div>
                </Card>
                </Link>
              ))}
              {replays.length === 0 && (
                <div className="col-span-full text-center py-12 text-muted-foreground/70">
                  <Radio className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No live replays yet</p>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Photos Gallery Section */}
        {activeSection === 'photos' && (
          <ListenerPhotoLibrary
            userId={profile.user_id}
            isOwnProfile={isOwnProfile}
            getAuthHeaders={getAuthHeaders}
            profile={profile}
          />
        )}
        
        {/* Photo Lightbox */}
        {photoLightbox.open && (
          <PhotoLightboxComponent
            userId={profile.user_id}
            userName={profile.name || profile.stage_name}
            initialPhotoUrl={profile.avatar_url}
            onClose={() => setPhotoLightbox({ open: false })}
          />
        )}

        {/* Spotlight Section */}
        {activeSection === 'spotlight' && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-foreground mb-4">Spotlight</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {posts.map((post) => (
                <Card 
                  key={post.post_id}
                  className="bg-muted/30 border-border p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => navigate(`/post/${post.post_id}`)}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-muted/50">
                      {profile.avatar_url ? (
                        <img src={profile.avatar_url.startsWith('http') ? profile.avatar_url : `${API.replace('/api', '')}${profile.avatar_url}`} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Music className="w-full h-full p-2 text-muted-foreground/70" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-foreground text-sm">{profile.name}</p>
                      <p className="text-xs text-muted-foreground/70">{new Date(post.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  
                  {post.content && (
                    <p className="text-foreground/80 text-sm mb-3 line-clamp-3">{post.content}</p>
                  )}
                  
                  {post.media_urls?.[0] && (
                    <div className="rounded-lg overflow-hidden mb-3">
                      <img src={post.media_urls[0]} alt="" className="w-full aspect-video object-cover" />
                    </div>
                  )}
                  
                  <div className="flex items-center gap-4 text-muted-foreground/70 text-sm">
                    <span className="flex items-center gap-1">
                      <Heart className="w-4 h-4" /> {post.likes_count || 0}
                    </span>
                    <span className="flex items-center gap-1">
                      <MessageCircle className="w-4 h-4" /> {post.comments_count || 0}
                    </span>
                  </div>
                </Card>
              ))}
              {posts.length === 0 && (
                <div className="col-span-full text-center py-12 text-muted-foreground/70">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No spotlight posts yet</p>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* About Section */}
        {activeSection === 'about' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-foreground mb-4">About {profile.name}</h2>
                <p className="text-muted-foreground leading-relaxed">
                  {profile.bio || 'No bio available.'}
                </p>
              </div>
              
              {/* Leaderboards */}
              <TopTippersLeaderboard creatorUserId={profile.user_id} />
              <TopFansLeaderboard creatorUserId={profile.user_id} />
              
              {profile.genres?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground/70 uppercase tracking-wider mb-3">Genres</h3>
                  <div className="flex flex-wrap gap-2">
                    {profile.genres.map((genre, i) => (
                      <Badge key={i} variant="secondary" className="bg-muted/50 text-foreground">
                        {genre}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {profile.neighborhood && (
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground/70 uppercase tracking-wider mb-3">Location</h3>
                  <p className="flex items-center gap-2 text-muted-foreground">
                    <MapPin className="w-4 h-4" />
                    {profile.neighborhood}
                  </p>
                </div>
              )}
            </div>
            
            <div className="space-y-6">
              {/* Stats */}
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground/70 uppercase tracking-wider mb-3">Stats</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-muted/30 rounded-lg p-4">
                    <p className="text-2xl font-bold text-foreground">{tracks.reduce((acc, t) => acc + (t.plays || 0), 0).toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground/70">Total Streams</p>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-4">
                    <p className="text-2xl font-bold text-foreground">{followerCount?.toLocaleString() || 0}</p>
                    <p className="text-sm text-muted-foreground/70">Followers</p>
                  </div>
                  {chartPosition && (
                    <div className="bg-muted/30 rounded-lg p-4">
                      <p className="text-2xl font-bold text-secondary">#{chartPosition}</p>
                      <p className="text-sm text-muted-foreground/70">Chart Position</p>
                    </div>
                  )}
                  <div className="bg-muted/30 rounded-lg p-4">
                    <p className="text-2xl font-bold text-foreground">{tracks.length}</p>
                    <p className="text-sm text-muted-foreground/70">Tracks</p>
                  </div>
                </div>
              </div>
              
              {/* Gamification */}
              {gamificationProfile && (
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground/70 uppercase tracking-wider mb-3">Level & Badges</h3>
                  <GamificationCard profile={gamificationProfile} />
                </div>
              )}

              {/* Influence & Top Supporters */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfluenceProfileCard userId={profile.user_id} getAuthHeaders={getAuthHeaders} />
                <TopSupportersCard creatorId={profile.user_id} />
              </div>

              {/* Social Links */}
              {profile.socials && Object.keys(profile.socials).length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground/70 uppercase tracking-wider mb-3">Connect</h3>
                  <div className="flex flex-wrap gap-3">
                    {Object.entries(profile.socials).map(([platform, url]) => {
                      if (!url) return null;
                      const Icon = SOCIAL_ICONS[platform] || Globe;
                      return (
                        <a
                          key={platform}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-4 py-2 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors text-muted-foreground hover:text-foreground"
                        >
                          <Icon className="w-4 h-4" />
                          <span className="text-sm capitalize">{platform}</span>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Sticky Play Bar for Mobile */}
      {latestRelease && (
        <div className="fixed bottom-0 left-0 right-0 md:hidden bg-background border-t border-border p-4 z-30">
          <Button
            onClick={playLatestRelease}
            className="w-full bg-gradient-to-r from-purple-600 to-violet-600 text-white"
          >
            <Play className="w-4 h-4 mr-2" fill="white" />
            Play Latest Release
          </Button>
        </div>
      )}
    </div>
    </ProfileBackground>
  );
}
